/**
 * AnalysisEventsRow - Displays maintenance event icons along a timeline.
 *
 * Shows event icons positioned along a horizontal timeline that aligns with
 * the chart X-axis. Each event type has a distinct icon and color.
 * Tooltips show event details on hover.
 */
import { useMemo } from 'react';
import { Paper, Text, Box, Tooltip } from '@mantine/core';
import { format, parseISO, isWithinInterval } from 'date-fns';
import type { TaskEvent } from '@/hooks/useAnalysisData';
import { TOOLTIP_DATE_FORMAT, TOOLTIP_DATE_ONLY_FORMAT } from './components/ChartTooltip';
import { getEventConfig } from './constants';

/** Base height of the events timeline in pixels */
const BASE_TIMELINE_HEIGHT = 40;

/** Vertical spacing between stacked icons */
const ICON_VERTICAL_SPACING = 32;

/** Icon size for event markers */
const ICON_SIZE = 18;

interface AnalysisEventsRowProps {
  /** List of selected event type names to display */
  selectedEvents: string[];
  /** All available maintenance events */
  events: TaskEvent[];
  /** Date range for positioning events */
  dateRange: {
    start: string;
    end: string;
  };
}

interface PositionedEvent extends TaskEvent {
  position: number; // Percentage position (0-100)
  stackIndex: number; // 0-based index within stack for same date
}

/**
 * Calculate position percentage for an event within the date range.
 * Uses the full datetime when available for precise positioning.
 * Returns a value between 0 and 100.
 */
function calculatePosition(
  eventDate: string,
  rangeStart: string,
  rangeEnd: string,
  eventDatetime?: string,
): number {
  // Use timestamps for precise positioning matching chart X-axis
  const startTs = new Date(`${rangeStart}T00:00:00`).getTime();
  const endTs = new Date(`${rangeEnd}T23:59:59.999`).getTime();
  const eventTs = eventDatetime
    ? new Date(eventDatetime).getTime()
    : new Date(`${eventDate}T12:00:00`).getTime();

  const totalRange = endTs - startTs;

  if (totalRange === 0) {
    return 50;
  }

  return ((eventTs - startTs) / totalRange) * 100;
}

/**
 * Check if an event date is within the date range (inclusive).
 */
function isEventInRange(
  eventDate: string,
  rangeStart: string,
  rangeEnd: string
): boolean {
  const eventDateParsed = parseISO(eventDate);
  const startDate = parseISO(rangeStart);
  const endDate = parseISO(rangeEnd);

  return isWithinInterval(eventDateParsed, { start: startDate, end: endDate });
}

/**
 * Filter and position events based on selection and date range.
 * Groups events by date and assigns stack indices for vertical stacking.
 */
function filterAndPositionEvents(
  selectedEvents: string[],
  events: TaskEvent[],
  dateRange: { start: string; end: string }
): { events: PositionedEvent[]; maxStack: number } {
  if (selectedEvents.length === 0 || events.length === 0) {
    return { events: [], maxStack: 0 };
  }

  const selectedSet = new Set(selectedEvents);

  // Filter and group by date
  const dateGroups = new Map<string, TaskEvent[]>();

  events.forEach((event) => {
    if (!selectedSet.has(event.type)) return;
    if (!isEventInRange(event.date, dateRange.start, dateRange.end)) return;

    const existing = dateGroups.get(event.date) || [];
    existing.push(event);
    dateGroups.set(event.date, existing);
  });

  // Flatten with stack indices
  const positioned: PositionedEvent[] = [];
  let maxStack = 0;

  dateGroups.forEach((eventsOnDate, date) => {
    maxStack = Math.max(maxStack, eventsOnDate.length);
    eventsOnDate.forEach((event, stackIndex) => {
      positioned.push({
        ...event,
        position: calculatePosition(date, dateRange.start, dateRange.end, event.datetime),
        stackIndex,
      });
    });
  });

  return {
    events: positioned.sort((a, b) => a.position - b.position),
    maxStack,
  };
}

/**
 * Tooltip content for an event.
 */
function EventTooltipContent({ event }: { event: TaskEvent }): JSX.Element {
  const formattedDate = event.datetime
    ? format(parseISO(event.datetime), TOOLTIP_DATE_FORMAT)
    : format(parseISO(event.date), TOOLTIP_DATE_ONLY_FORMAT);

  return (
    <Box>
      <Text fw={600} size="sm" mb={2}>
        {event.type}
      </Text>
      <Text size="xs" c="dimmed">
        {formattedDate}
      </Text>
      {event.notes && (
        <Text size="xs" mt={4} style={{ maxWidth: 220 }}>
          {event.notes}
        </Text>
      )}
    </Box>
  );
}

/**
 * Single event icon with tooltip.
 */
function EventIcon({
  event,
  position,
  stackIndex,
}: {
  event: TaskEvent;
  position: number;
  stackIndex: number;
}): JSX.Element {
  const config = getEventConfig(event.type);
  const Icon = config.icon;

  // Calculate vertical offset: first icon at center, subsequent icons above
  const verticalOffset = stackIndex * ICON_VERTICAL_SPACING;

  return (
    <Tooltip
      label={<EventTooltipContent event={event} />}
      position="top"
      withArrow
      multiline
      maw={250}
    >
      <Box
        data-testid="event-icon"
        data-event-type={event.type}
        style={{
          position: 'absolute',
          left: `${position}%`,
          bottom: `calc(50% - 14px + ${verticalOffset}px)`,
          transform: 'translateX(-50%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 28,
          height: 28,
          borderRadius: '50%',
          backgroundColor: `${config.color}20`, // 20% opacity background
          border: `2px solid ${config.color}`,
          cursor: 'pointer',
          zIndex: 1,
        }}
      >
        <Icon size={ICON_SIZE} color={config.color} />
      </Box>
    </Tooltip>
  );
}

/**
 * Events row showing maintenance event icons along a timeline.
 * Returns null if no events are selected or no matching events exist.
 */
export function AnalysisEventsRow({
  selectedEvents,
  events,
  dateRange,
}: AnalysisEventsRowProps): JSX.Element | null {
  // Filter and position events
  const { events: positionedEvents, maxStack } = useMemo(
    () => filterAndPositionEvents(selectedEvents, events, dateRange),
    [selectedEvents, events, dateRange]
  );

  // Return null if no matching events
  if (positionedEvents.length === 0) {
    return null;
  }

  // Dynamic height based on max stack depth
  const timelineHeight = BASE_TIMELINE_HEIGHT + (maxStack - 1) * ICON_VERTICAL_SPACING;

  return (
    <Paper p="md" withBorder radius="md" data-testid="events-row">
      <Text fw={600} size="sm" mb="sm">
        Events
      </Text>
      <Box
        data-testid="events-timeline"
        style={{
          position: 'relative',
          height: `${timelineHeight}px`,
          backgroundColor: 'var(--mantine-color-gray-1)',
          borderRadius: 'var(--mantine-radius-sm)',
          marginLeft: 60, // Match chart left margin (10) + Y-axis width (50)
          marginRight: 30, // Match chart right margin
        }}
      >
        {/* Timeline line - positioned at bottom */}
        <Box
          style={{
            position: 'absolute',
            left: 0,
            right: 0,
            bottom: '50%',
            transform: 'translateY(50%)',
            height: 2,
            backgroundColor: 'var(--mantine-color-gray-4)',
          }}
        />
        {/* Event icons */}
        {positionedEvents.map((event, index) => (
          <EventIcon
            key={`${event.date}-${event.type}-${index}`}
            event={event}
            position={event.position}
            stackIndex={event.stackIndex}
          />
        ))}
      </Box>
    </Paper>
  );
}
